<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Nav -->
      <nav class="nav">
        <img :src="lightLogoUrl" class="nav-logo" />
        <div class="nav-mid">
          <span class="nav-lo">{{ loName || "Loan Officer" }}</span>
          <span class="nav-plus">+</span>
          <span class="nav-re">{{ realtorName || "Realtor" }}</span>
        </div>
   
      </nav>

      <!-- ARCH HERO -->
      <section class="arch-section">
        <!-- The arch shape itself -->
        <div class="arch-frame">
          <div class="arch-inner">
         
            <h1 class="arch-h1" v-html="heroH1Html"></h1>
            <p class="arch-body">{{ heroBody }}</p>
            <div class="arch-btns">
              <a class="btn-light" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="btn-outline-light" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
          </div>
        </div>

        <!-- Quick contact row beneath arch -->
        <div class="quick-row">
          <a v-if="loinfo && loinfo.phone_number" class="qr-pill" :href="'tel:' + loinfo.phone_number">Call us</a>
          <a v-if="loinfo && loinfo.email" class="qr-pill" :href="'mailto:' + loinfo.email">Email us</a>
          <a v-if="realtor && realtor.website_url" class="qr-pill" :href="realtor.website_url" target="_blank" rel="noreferrer">Partner site</a>
        </div>
      </section>

      <!-- PROFILE CARDS — horizontal row -->
      <section class="profile-row">

        <div class="pcard lo-pcard">
          <div class="pcard-img-wrap">
            <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="pcard-img" />
          </div>
          <div class="pcard-body">
            <span class="pcard-label">Loan Officer</span>
            <div class="pcard-name">{{ loName }}</div>
            <div class="pcard-role">{{ loTitle }}</div>
            <div v-if="loinfo && loinfo.nmls" class="pcard-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
            <div class="pcard-contacts">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="pcard-chip">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="pcard-chip">{{ loinfo.email }}</a>
            </div>
         
          </div>
        </div>

        <div class="pcard re-pcard">
          <div class="pcard-img-wrap re-img-wrap">
            <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="pcard-img" />
          </div>
          <div class="pcard-body">
            <span class="pcard-label re-label">{{ realtor?.partner_title || 'Partner' }}</span>
            <div class="pcard-name">{{ realtorName }}</div>
            <div class="pcard-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
            <div v-if="realtor && realtor.nmls" class="pcard-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
            <div class="pcard-contacts">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="pcard-chip re-chip">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="pcard-chip re-chip">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="pcard-chip re-chip" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>
        </div>

      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandArch",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    lightLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Young+Serif&family=Figtree:wght@300;400;500;600;700&display=swap');

.page {
  min-height: 100vh;
  background: #f6f3ee;
  color: #1c1a17;
  font-family: 'Figtree', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --hairline: rgba(28,26,23,0.10);
  --muted: #78716c;
  --card-bg: #ffffff;
  --a-tint: color-mix(in srgb, var(--a) 12%, #f6f3ee);
  --p-tint: color-mix(in srgb, var(--p) 5%, #f6f3ee);
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* NAV */
.nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 48px;
  background: #f6f3ee;
}
.nav-logo { width: 160px; height: auto; object-fit: contain; }
.nav-mid { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.nav-lo { font-weight: 600; color: var(--p); }
.nav-plus { color: var(--a); font-size: 18px; line-height: 1; }
.nav-re { color: var(--muted); }
.nav-cta {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 10px 22px; border-radius: 50px; transition: opacity 0.2s;
}
.nav-cta:hover { opacity: 0.85; }

/* ARCH SECTION */
.arch-section { padding: 0 40px 48px; display: flex; flex-direction: column; align-items: center; }

/* The arch is a tall rounded rectangle — border-radius on top only */
.arch-frame {
  width: 100%;
  max-width: 780px;
  background: var(--p);
  border-radius: 999px 999px 48px 48px;
  padding: 214px 60px 64px;
  position: relative;
  overflow: hidden;
  text-align: center;
}
/* Subtle glow inside arch */
.arch-frame::before {
  content: "";
  position: absolute; top: -80px; left: 50%; transform: translateX(-50%);
  width: 500px; height: 300px; border-radius: 50%;
  background: radial-gradient(ellipse, color-mix(in srgb, var(--a) 25%, transparent), transparent 70%);
  pointer-events: none;
}
.arch-inner { position: relative; z-index: 1; }
.arch-kicker { font-size: 11px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: var(--a); margin-bottom: 16px; }
.arch-h1 {
  font-family: 'Young Serif', serif;
  font-size: clamp(38px, 5.5vw, 66px);
  font-weight: 400; line-height: 1.1;
  color: #fff; margin: 0 0 18px;
}
.arch-h1 em { font-style: italic; color: var(--a); }
.arch-body { font-size: 15px; line-height: 1.75; color: rgba(255,255,255,0.58);  margin: 0 auto 28px; }
.arch-btns { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; }
.btn-light {
  font-size: 14px; font-weight: 600; text-decoration: none;
  background: var(--a); color: var(--p);
  padding: 13px 28px; border-radius: 50px;
  box-shadow: 0 4px 18px color-mix(in srgb, var(--a) 40%, transparent);
  transition: transform 0.18s, box-shadow 0.18s;
  width:55%
}
.btn-light:hover { transform: translateY(-1px); box-shadow: 0 8px 26px color-mix(in srgb, var(--a) 50%, transparent); }
.btn-outline-light {
  font-size: 14px; font-weight: 500; text-decoration: none;
  color: rgba(255,255,255,0.75);
  border: 1.5px solid rgba(255,255,255,0.22);
  padding: 13px 28px; border-radius: 50px;
  transition: background 0.18s;
}
.btn-outline-light:hover { background: rgba(255,255,255,0.10); }

/* QUICK ROW */
.quick-row {
  display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;
  margin-top: 20px;
}
.qr-pill {
  font-size: 12px; font-weight: 500; text-decoration: none;
  color: var(--muted); background: var(--card-bg);
  border: 1px solid var(--hairline);
  padding: 7px 16px; border-radius: 50px;
  transition: all 0.18s;
}
.qr-pill:hover { color: var(--p); border-color: color-mix(in srgb, var(--p) 20%, transparent); }

/* PROFILE ROW */
.profile-row {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 14px; max-width: 860px;
  margin: 0 auto; padding: 0 40px 60px; width: 100%;
}
.pcard {
  background: var(--card-bg); border-radius: 22px;
  border: 1px solid var(--hairline);
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  transition: transform 0.2s, box-shadow 0.2s;
}
.pcard:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(0,0,0,0.10); }

.pcard-img-wrap {
  height: 100px;
  background: color-mix(in srgb, var(--p) 8%, #f6f3ee);
  display: flex; align-items: flex-end; justify-content: flex-start;
  padding: 0 0 0 22px; position: relative;
}
.re-img-wrap { background: color-mix(in srgb, var(--a) 10%, #f6f3ee); }

.pcard-img {
  width: 72px;  border-radius: 14px; object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  transform: translateY(36px);
}

.pcard-body { padding: 44px 22px 22px; }
.pcard-label { font-size: 9px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); display: block; margin-bottom: 6px; }
.re-label { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.pcard-name { font-family: 'Young Serif', serif; font-size: 21px; font-weight: 400; color: var(--p); margin-bottom: 3px; }
.pcard-role { font-size: 12px; color: var(--muted); margin-bottom: 2px; }
.pcard-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 60%, transparent); margin-bottom: 14px; }
.pcard-contacts { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.pcard-chip {
  font-size: 11px; font-weight: 500; text-decoration: none;
  background: var(--p-tint); color: var(--p);
  padding: 5px 12px; border-radius: 50px;
  border: 1px solid var(--hairline); transition: background 0.18s;
}
.pcard-chip:hover { background: color-mix(in srgb, var(--p) 10%, #f6f3ee); }
.re-chip { background: var(--a-tint); color: color-mix(in srgb, var(--a) 85%, var(--p)); border-color: color-mix(in srgb, var(--a) 18%, transparent); }
.re-chip:hover { background: color-mix(in srgb, var(--a) 18%, #f6f3ee); }
.pcard-cta {
  display: block; text-align: center; text-decoration: none;
  background: var(--p); color: #fff;
  font-size: 13px; font-weight: 600; padding: 11px;
  border-radius: 12px; transition: opacity 0.18s;
}
.pcard-cta:hover { opacity: 0.85; }

@media (max-width: 680px) {
  .arch-frame { border-radius: 32px; padding: 82px 28px 44px; }
  .profile-row { grid-template-columns: 1fr; padding: 0 20px 40px; }
  .arch-section { padding: 0 20px 36px; }
  .nav { padding: 16px 24px; }
  .nav-mid { display: none; }
}
</style>
